import { ReactNode, useEffect, useState } from 'react';

import { CatalogFaq } from '@/lib/entities/catalog/ui/catalog-faq/catalog-faq';
import {
  CONCIERGE_HERO_IMAGE,
  CONCIERGE_STATS_LEAF_IMAGE,
  ConciergeCatalogItem,
  ConciergeCatalogPage,
  ConciergeHero,
  ConciergeHubPage,
  ConciergeItemCatalog,
  ConciergeServicePage,
  conciergeStatsIcons,
  getCatalogFallbackImage,
  getCatalogListingHref,
  getCatalogPageQuery,
  getConciergeCatalogImageSrc,
  getConciergeItemHref,
  getConciergeServiceImageSrc,
  hasConciergeCatalogImage,
  hasConciergeServiceImage,
} from '@/lib/entities/concierge';

import { Breadcrumbs } from '@/lib/shared/components/Breadcrumbs';
import { Button, CircleButton } from '@/lib/shared/components/Buttons';
import { modal } from '@/lib/shared/components/Modal';
import { PopupCloseButton } from '@/lib/shared/components/Modal/PopupCloseButton';
import { Pagination } from '@/lib/shared/components/Pagination';
import { H1, H2, H3, P2, P3, P4 } from '@/lib/shared/components/Typography';
import {
  ArrowIcon,
  CabinIcon,
  GuestsIcon,
  SearchIcon,
  SpeedIcon,
  YearIcon,
} from '@/lib/shared/icons';

import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { CatalogModalContent } from './CatalogModalContent';
import { ConciergeCatalogSection } from './ConciergeCatalogSection';
import styles from './ConciergePages.module.scss';
import { openConciergeLeadModal } from './openConciergeLeadModal';

const uiCopy = {
  ru: {
    open: 'Открыть',
    details: 'Подробнее',
    book: 'Забронировать',
    search: 'Поиск',
  },
  en: {
    open: 'Open',
    details: 'Details',
    book: 'Reserve',
    search: 'Search',
  },
} as const;

const openConciergeRequestModal = (
  sourceForm: string = 'concierge_hero',
  comment?: string
) => {
  openConciergeLeadModal({ sourceForm, comment });
};

const ConciergeHeroSection = ({
  hero,
  onButtonClick,
}: {
  hero: ConciergeHero;
  onButtonClick?: () => void;
}) => {
  const handleButtonClick =
    onButtonClick ?? (() => openConciergeRequestModal());

  return (
    <section className={styles.conciergeHero}>
      <div className={styles.conciergeHero__media} aria-hidden>
        <Image
          src={CONCIERGE_HERO_IMAGE.mobile}
          alt=""
          width={600}
          height={337}
          priority
          loader={({ src }) => src}
          className={clsx(
            styles.conciergeHero__bg,
            styles['conciergeHero__bg--mobile']
          )}
          sizes="100vw"
        />
        <Image
          src={CONCIERGE_HERO_IMAGE.desktop}
          alt=""
          width={1344}
          height={756}
          priority
          loader={({ src }) => src}
          className={clsx(
            styles.conciergeHero__bg,
            styles['conciergeHero__bg--desktop']
          )}
          sizes="(min-width: 768px) 1344px, 100vw"
        />
      </div>
      <div className={styles.conciergeHero__overlay} aria-hidden />
      <div className={styles.conciergeHero__gradient} aria-hidden />
      <div className={styles.conciergeHero__content}>
        <H1 className={styles.conciergeHero__title}>{hero.title}</H1>
        {hero.description ? (
          <P3 className={styles.conciergeHero__text}>{hero.description}</P3>
        ) : null}
        <Button
          as="button"
          type="button"
          label={hero.buttonLabel ?? uiCopy.ru.open}
          size="lg"
          theme="light"
          icon={<ArrowIcon isRight />}
          className={styles.conciergeHero__button}
          onClick={handleButtonClick}
        />
      </div>
    </section>
  );
};

const ConciergeInnerHeroMedia = ({
  image,
  video,
  position,
  variantClassName,
  width,
  height,
  sizes,
}: {
  image: string;
  video?: string;
  position?: string;
  variantClassName: string;
  width: number;
  height: number;
  sizes: string;
}) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  if (!video) {
    return (
      <Image
        src={image}
        alt=""
        width={width}
        height={height}
        priority
        loader={({ src }) => src}
        className={clsx(styles.conciergeInnerHero__bg, variantClassName)}
        style={position ? { objectPosition: position } : undefined}
        sizes={sizes}
      />
    );
  }

  return (
    <>
      <video
        autoPlay
        muted
        playsInline
        loop
        poster={image}
        onPlaying={() => setIsVideoPlaying(true)}
        className={clsx(styles.conciergeInnerHero__bg, variantClassName)}
        style={position ? { objectPosition: position } : undefined}
      >
        <source src={video} type="video/mp4" />
        <track kind="captions" style={{ display: 'none' }}></track>
      </video>
      <Image
        src={image}
        alt=""
        width={width}
        height={height}
        priority
        loader={({ src }) => src}
        className={clsx(
          styles.conciergeInnerHero__bg,
          styles.conciergeInnerHero__poster,
          variantClassName,
          isVideoPlaying && styles['conciergeInnerHero__poster--hidden']
        )}
        style={position ? { objectPosition: position } : undefined}
        sizes={sizes}
      />
    </>
  );
};

const ConciergeInnerHeroSection = ({
  hero,
  locale,
}: {
  hero: ConciergeHero;
  locale: 'ru' | 'en';
}) => {
  const desktopImage = hero.desktopImage || CONCIERGE_HERO_IMAGE.desktop;
  const mobileImage =
    hero.mobileImage || hero.desktopImage || CONCIERGE_HERO_IMAGE.mobile;
  const desktopImagePosition = hero.desktopImagePosition;
  const mobileImagePosition = hero.mobileImagePosition;
  const desktopVideo = hero.desktopVideo;
  const mobileVideo = hero.mobileVideo || hero.desktopVideo;

  return (
    <section className={styles.conciergeInnerHero}>
      <div className={styles.conciergeInnerHero__media} aria-hidden>
        <ConciergeInnerHeroMedia
          image={mobileImage}
          video={mobileVideo}
          position={mobileImagePosition}
          variantClassName={styles['conciergeInnerHero__bg--mobile']}
          width={600}
          height={337}
          sizes="100vw"
        />
        <ConciergeInnerHeroMedia
          image={desktopImage}
          video={desktopVideo}
          position={desktopImagePosition}
          variantClassName={styles['conciergeInnerHero__bg--desktop']}
          width={1344}
          height={331}
          sizes="(min-width: 768px) 1344px, 100vw"
        />
      </div>
      <div className={styles.conciergeInnerHero__overlay} aria-hidden />
      <div className={styles.conciergeInnerHero__gradient} aria-hidden />
      <div className={styles.conciergeInnerHero__content}>
        <H2 as="h1" className={styles.conciergeInnerHero__title}>
          {hero.title}
        </H2>
        <Button
          as="button"
          type="button"
          label={
            locale === 'ru' ? 'Написать в WhatsApp' : 'Message on WhatsApp'
          }
          size="lg"
          theme="light"
          icon={<ArrowIcon isRight />}
          className={styles.conciergeInnerHero__button}
          onClick={() => openConciergeRequestModal()}
        />
      </div>
    </section>
  );
};

const OverviewGallerySlider = ({
  images,
  caption,
  altPrefix,
}: {
  images: string[];
  caption?: string;
  altPrefix?: string;
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: images.length > 1 });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(images.length > 1);

  useEffect(() => {
    if (!emblaApi) return;

    const updateState = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    updateState();
    emblaApi.on('select', updateState);
    emblaApi.on('reInit', updateState);

    return () => {
      emblaApi.off('select', updateState);
      emblaApi.off('reInit', updateState);
    };
  }, [emblaApi]);

  return (
    <div className={styles.serviceOverviewGallery}>
      <div className={styles.serviceOverviewGallery__viewport} ref={emblaRef}>
        <div className={styles.serviceOverviewGallery__container}>
          {images.map((image, index) => (
            <div
              className={styles.serviceOverviewGallery__slide}
              key={`${image}-${index}`}
            >
              <div className={styles.serviceOverviewGallery__media}>
                <Image
                  src={image}
                  alt={altPrefix ? `${altPrefix} — ${index + 1}` : ''}
                  fill
                  sizes="(min-width: 996px) 525px, 100vw"
                  loader={({ src }) => src}
                  className={styles.serviceOverviewGallery__image}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.serviceOverviewGallery__controls} aria-hidden>
        <CircleButton
          as="button"
          type="button"
          theme="dark"
          name="Previous slide"
          className={styles.serviceOverviewGallery__control}
          content={<ArrowIcon />}
          disabled={!canScrollPrev}
          onClick={() => emblaApi?.scrollPrev()}
        />
        <CircleButton
          as="button"
          type="button"
          theme="dark"
          name="Next slide"
          className={styles.serviceOverviewGallery__control}
          content={<ArrowIcon isRight />}
          disabled={!canScrollNext}
          onClick={() => emblaApi?.scrollNext()}
        />
      </div>

      {caption ? (
        <P3 className={styles.serviceOverviewGallery__caption}>{caption}</P3>
      ) : null}
    </div>
  );
};

const ServiceOverviewSection = ({
  overview,
}: {
  overview: ConciergeServicePage['overview'];
}) => {
  return (
    <section className={styles.serviceOverview}>
      <div className={styles.serviceOverview__content}>
        <div className={styles.serviceOverview__copy}>
          <H2 className={styles.serviceOverview__title}>{overview.title}</H2>
          <div className={styles.serviceOverview__details}>
            {overview.detailsBlocks.map((block, index) => (
              <div className={styles.serviceOverview__detailBlock} key={index}>
                {block.map((line) => (
                  <P3 className={styles.serviceOverview__detailLine} key={line}>
                    {line}
                  </P3>
                ))}
              </div>
            ))}
          </div>
          {overview.gallery?.images.length ? (
            <OverviewGallerySlider
              images={overview.gallery.images}
              caption={overview.gallery.caption}
              altPrefix={overview.title}
            />
          ) : null}
          <Button
            as="button"
            type="button"
            label={overview.buttonLabel}
            size="lg"
            theme="dark"
            icon={<ArrowIcon isRight />}
            className={styles.serviceOverview__button}
            onClick={() => openConciergeRequestModal()}
          />
        </div>

        <div className={styles.serviceOverview__cards}>
          {overview.cards.map((card) => {
            const hasDetailRows = Boolean(card.detailRows?.length);

            return (
              <article
                className={clsx(
                  styles.serviceOverviewCard,
                  hasDetailRows && styles['serviceOverviewCard--withRows']
                )}
                key={card.title}
              >
                <div className={styles.serviceOverviewCard__main}>
                  <H3 className={styles.serviceOverviewCard__title}>
                    {card.title}
                  </H3>
                  {card.description ? (
                    <P3 className={styles.serviceOverviewCard__description}>
                      {card.description}
                    </P3>
                  ) : null}
                  {hasDetailRows ? (
                    <div className={styles.serviceOverviewCard__rows}>
                      {card.detailRows!.map((row) => (
                        <div
                          className={styles.serviceOverviewCard__row}
                          key={row.label}
                        >
                          <P3 className={styles.serviceOverviewCard__rowLabel}>
                            {row.label}
                          </P3>
                          <P2 className={styles.serviceOverviewCard__rowValue}>
                            {row.value}
                          </P2>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
                {card.price ? (
                  <P2 className={styles.serviceOverviewCard__price}>
                    {card.price}
                  </P2>
                ) : null}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const ConciergeCatalogModal = ({
  item,
  locale,
  assetCollection,
}: {
  item: ConciergeCatalogItem;
  locale: 'ru' | 'en';
  assetCollection?: ConciergeCatalogPage['assetCollection'];
}) => {
  return (
    <div className={styles.conciergeCatalogModal}>
      <div className={styles.conciergeCatalogModal__shell}>
        <div className={styles.conciergeCatalogModal__header}>
          <H3 className={styles.conciergeCatalogModal__title}>{item.title}</H3>
          <PopupCloseButton
            className={styles.conciergeCatalogModal__close}
            onClick={() => modal.close()}
          />
        </div>
        <div className={styles.conciergeCatalogModal__divider} />
        <CatalogModalContent
          item={item}
          locale={locale}
          assetCollection={assetCollection}
        />
      </div>
    </div>
  );
};

const openCatalogModal = (
  item: ConciergeCatalogItem,
  locale: 'ru' | 'en',
  assetCollection?: ConciergeCatalogPage['assetCollection']
) => {
  modal.open(
    <ConciergeCatalogModal
      item={item}
      locale={locale}
      assetCollection={assetCollection}
    />,
    {
      fullScreen: true,
      hideCloseButton: true,
      skipUrlSync: true,
    }
  );
};

const CatalogCard = ({
  item,
  locale,
  assetCollection,
}: {
  item: ConciergeCatalogItem;
  locale: 'ru' | 'en';
  assetCollection?: ConciergeCatalogPage['assetCollection'];
}) => {
  const accent = item.accent || 'ocean';
  const copy = uiCopy[locale];
  const handleOpen = () => openCatalogModal(item, locale, assetCollection);

  return (
    <article className={styles.catalogCard}>
      <button
        type="button"
        aria-label={item.title}
        className={styles.catalogCard__action}
        onClick={handleOpen}
      />
      <div
        className={clsx(
          styles.catalogCard__visual,
          styles[`catalogCard__visual--${accent}`]
        )}
      >
        <div className={styles.catalogCard__visualGlow} />
        <H2 as="h3" className={styles.catalogCard__visualTitle}>
          {item.title}
        </H2>
      </div>
      <div className={styles.catalogCard__body}>
        <P4 className={styles.catalogCard__subtitle}>{item.subtitle}</P4>
        <P2 className={styles.catalogCard__price}>{item.price}</P2>
        <P3 className={styles.catalogCard__summary}>{item.summary}</P3>
        <div className={styles.catalogCard__footer}>
          <P4>{copy.details}</P4>
          <CircleButton
            as="button"
            type="button"
            theme="mixed"
            name={item.title}
            className={styles.catalogCard__arrow}
            content={<ArrowIcon isRight />}
            onClick={handleOpen}
          />
        </div>
      </div>
    </article>
  );
};

const CatalogRootTileCard = ({
  item,
  locale,
  assetCollection,
  itemCatalog,
}: {
  item: ConciergeCatalogItem;
  locale: 'ru' | 'en';
  assetCollection?: ConciergeCatalogPage['assetCollection'];
  itemCatalog?: ConciergeItemCatalog;
}) => {
  const accent = item.accent || 'ocean';
  const imageKey = item.imageKey || item.slug;
  const catalogImageName = item.imageName || 'cover';
  const hasExplicitImageKey = Boolean(item.imageKey);
  const hasCatalogImage =
    !hasExplicitImageKey && assetCollection && item.assetFolder
      ? hasConciergeCatalogImage({
          collection: assetCollection,
          assetFolder: item.assetFolder!,
          imageName: catalogImageName,
        })
      : false;
  const imageSrc = hasExplicitImageKey
    ? getConciergeServiceImageSrc(imageKey)
    : assetCollection && item.assetFolder
      ? hasCatalogImage || !getCatalogFallbackImage(assetCollection)
        ? getConciergeCatalogImageSrc({
            collection: assetCollection,
            assetFolder: item.assetFolder,
            imageName: catalogImageName,
          })
        : getCatalogFallbackImage(assetCollection)
      : getConciergeServiceImageSrc(imageKey);
  const hasImage = hasExplicitImageKey
    ? hasConciergeServiceImage(imageKey)
    : assetCollection && item.assetFolder
      ? hasCatalogImage || Boolean(getCatalogFallbackImage(assetCollection))
      : hasConciergeServiceImage(imageKey);
  const placeholderLabel = hasExplicitImageKey
    ? imageKey
    : assetCollection && item.assetFolder
      ? `${item.assetFolder}/${catalogImageName}`
      : imageKey;
  const itemHref =
    itemCatalog && assetCollection !== 'car'
      ? getConciergeItemHref(itemCatalog, item.slug)
      : null;
  const handleOpen = () => {
    if (itemHref) {
      return;
    }

    if (assetCollection === 'car') {
      openConciergeRequestModal(
        `concierge_${assetCollection}_${item.slug}`,
        item.title
      );

      return;
    }

    openCatalogModal(item, locale, assetCollection);
  };

  return (
    <article
      className={clsx(
        styles.rootTile,
        assetCollection === 'yacht' && styles.rootTileYacht
      )}
    >
      {itemHref ? (
        <Link
          href={itemHref}
          className={styles.rootTile__action}
          aria-label={item.title}
        />
      ) : (
        <button
          type="button"
          className={styles.rootTile__action}
          onClick={handleOpen}
        />
      )}
      <div
        className={clsx(
          styles.rootTile__media,
          styles[`rootTile__media--${accent}`],
          hasImage && styles['rootTile__media--hasImage']
        )}
      >
        {hasImage ? (
          <Image
            src={imageSrc}
            alt={item.title}
            fill
            sizes="(min-width: 996px) 33vw, (min-width: 768px) 50vw, 100vw"
            loader={({ src }) => src}
            className={styles.rootTile__image}
          />
        ) : (
          <div className={styles.rootTile__assetPlaceholder} aria-hidden>
            {placeholderLabel}
          </div>
        )}
      </div>
      <div className={styles.rootTile__body}>
        <div className={styles.rootTile__copy}>
          <H3 className={styles.rootTile__title}>{item.title}</H3>
          {item.specs ? (
            <ul className={styles.rootTile__specs}>
              {item.specs.map((spec) => (
                <li key={spec.icon} className={styles.rootTile__spec}>
                  {spec.icon === 'speed' && (
                    <SpeedIcon className={styles.rootTile__specIcon} />
                  )}
                  {spec.icon === 'guests' && (
                    <GuestsIcon className={styles.rootTile__specIcon} />
                  )}
                  {spec.icon === 'cabins' && (
                    <CabinIcon className={styles.rootTile__specIcon} />
                  )}
                  {spec.icon === 'year' && (
                    <YearIcon className={styles.rootTile__specIcon} />
                  )}
                  <span>{spec.label}</span>
                </li>
              ))}
            </ul>
          ) : (
            <P3 className={styles.rootTile__description}>{item.summary}</P3>
          )}
        </div>
        <div className={styles.rootTile__divider} aria-hidden />
        <div className={styles.rootTile__bottom}>
          <p className={styles.rootTile__price}>{item.price}</p>
          {itemHref ? (
            <CircleButton
              as="button"
              type="button"
              tabIndex={-1}
              aria-hidden
              theme="mixed"
              className={clsx(
                styles.rootTile__arrow,
                styles['rootTile__arrow--decorative']
              )}
              content={<ArrowIcon isRight />}
            />
          ) : (
            <CircleButton
              as="button"
              type="button"
              theme="mixed"
              name={item.title}
              className={styles.rootTile__arrow}
              content={<ArrowIcon isRight />}
              onClick={handleOpen}
            />
          )}
        </div>
      </div>
    </article>
  );
};

const getHeroWithSeoTitle = (
  hero: ConciergeHero,
  h1?: string | null
): ConciergeHero => ({
  ...hero,
  title: h1 || hero.title,
});

const PageShell = ({
  breadcrumbs,
  hero,
  locale,
  h1,
  children,
}: {
  breadcrumbs: Array<{ title: string; link?: string }>;
  hero: ConciergeHero;
  locale: 'ru' | 'en';
  h1?: string | null;
  children: ReactNode;
}) => {
  return (
    <main className={styles.page}>
      <div className="container">
        <Breadcrumbs items={breadcrumbs} className={styles.breadcrumbs} />
        <ConciergeInnerHeroSection
          hero={getHeroWithSeoTitle(hero, h1)}
          locale={locale}
        />
      </div>
      {children}
    </main>
  );
};

export const ConciergeHubPageView = ({
  page,
  locale,
  h1,
}: {
  page: ConciergeHubPage;
  locale: 'ru' | 'en';
  h1?: string | null;
}) => {
  const handleCatalogClick = () => {
    document
      .getElementById('concierge-catalog')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main className={styles.page}>
      <div className="container">
        <ConciergeHeroSection
          hero={getHeroWithSeoTitle(page.hero, h1)}
          onButtonClick={handleCatalogClick}
        />
      </div>

      <section
        className={styles.statsBand}
        aria-labelledby="concierge-stats-title"
      >
        <div className={styles.statsBand__decor} aria-hidden>
          <div className={styles.statsBand__decorTransform}>
            <div className={styles.statsBand__decorMedia}>
              <Image
                src={CONCIERGE_STATS_LEAF_IMAGE}
                alt=""
                fill
                sizes="(min-width: 996px) 1060px, 520px"
                loader={({ src }) => src}
                className={styles.statsBand__decorImage}
              />
            </div>
          </div>
        </div>
        <div className={clsx('container', styles.statsBand__inner)}>
          <div className={styles.statsBand__top}>
            <H2 className={styles.statsBand__title} id="concierge-stats-title">
              {page.statsTitle}
            </H2>
            <P3 className={styles.statsBand__text}>{page.statsText}</P3>
          </div>
          <div className={styles.statsBand__grid}>
            {page.stats.map((stat) => (
              <article key={stat.iconKey} className={styles.statsBand__card}>
                <H3 className={styles.statsBand__value}>{stat.value}</H3>
                <P3 className={styles.statsBand__label}>{stat.label}</P3>
                <div className={styles.statsBand__icon} aria-hidden>
                  {conciergeStatsIcons[stat.iconKey]}
                </div>
              </article>
            ))}
          </div>
          <div className={styles.statsBand__divider} aria-hidden />
        </div>
      </section>

      <div className="container">
        <ConciergeCatalogSection page={page} locale={locale} />
      </div>
    </main>
  );
};

export const ConciergeServicePageView = ({
  page,
  locale: _locale,
  h1,
}: {
  page: ConciergeServicePage;
  locale: 'ru' | 'en';
  h1?: string | null;
}) => {
  return (
    <PageShell
      breadcrumbs={page.breadcrumbs}
      hero={page.hero}
      locale={_locale}
      h1={h1}
    >
      <div className="container">
        <ServiceOverviewSection overview={page.overview} />
      </div>
      <CatalogFaq faq={page.faq ? [...page.faq] : null} />
    </PageShell>
  );
};

export const ConciergeCatalogPageView = ({
  page,
  locale,
  h1,
  itemCatalog,
}: {
  page: ConciergeCatalogPage;
  locale: 'ru' | 'en';
  h1?: string | null;
  itemCatalog?: ConciergeItemCatalog;
}) => {
  const router = useRouter();
  const [searchValue, setSearchValue] = useState('');
  const flatItems = page.groups.flatMap((group) => group.items);
  const normalizedSearch = searchValue.trim().toLowerCase();
  const hasSearch = Boolean(
    page.searchPlaceholder || page.searchEmpty || page.searchDisclaimer
  );
  const filteredItems = normalizedSearch
    ? flatItems.filter((item) =>
        [
          item.title,
          item.subtitle,
          item.summary,
          ...item.bullets,
          ...(item.specs?.map((spec) => spec.label) ?? []),
        ].some((value) => value.toLowerCase().includes(normalizedSearch))
      )
    : flatItems;
  const pageSize = page.pageSize || flatItems.length;
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const queryPage = router.isReady ? Number(router.query.page) : NaN;
  const currentPage =
    Number.isFinite(queryPage) && queryPage > 0 ? queryPage : 1;
  const normalizedPage = Math.min(currentPage, totalPages);
  const pageStart = (normalizedPage - 1) * pageSize;
  const paginatedItems = filteredItems.slice(pageStart, pageStart + pageSize);

  const updatePageQuery = (nextPage: number) => {
    if (!router.isReady) return;
    router.replace(
      {
        pathname: router.pathname,
        query: getCatalogPageQuery(router.query, nextPage),
      },
      getCatalogListingHref(router.asPath, nextPage),
      { shallow: true, scroll: true }
    );
  };

  const handleJumpToPage = (page: number) => updatePageQuery(page);
  const handleSetNext = () =>
    updatePageQuery(Math.min(totalPages, normalizedPage + 1));
  const handleSetPrev = () => updatePageQuery(Math.max(1, normalizedPage - 1));

  return (
    <PageShell
      breadcrumbs={page.breadcrumbs}
      hero={page.hero}
      locale={locale}
      h1={h1}
    >
      <div className="container">
        <div className={styles.contentCopy}>
          {page.layout === 'rootTiles' ? (
            <div
              className={clsx(
                styles.rootCatalog__layoutHead,
                !page.introTitle && styles.rootCatalog__layoutHeadNoTitle,
                !hasSearch && styles.rootCatalog__layoutHeadNoSearch,
                page.assetCollection === 'yacht' &&
                  styles.rootCatalog__layoutHeadYacht
              )}
            >
              {page.introTitle ? (
                <H2
                  className={clsx(
                    styles.contentTitle,
                    styles.rootCatalog__title,
                    page.assetCollection === 'yacht' && styles.contentTitleYacht
                  )}
                >
                  {page.introTitle}
                </H2>
              ) : null}
              {hasSearch ? (
                <div className={styles.rootCatalog__searchColumn}>
                  <label className={styles.rootCatalog__search}>
                    <input
                      type="search"
                      value={searchValue}
                      onChange={(event) => setSearchValue(event.target.value)}
                      placeholder={
                        page.searchPlaceholder || uiCopy[locale].search
                      }
                      className={styles.rootCatalog__searchInput}
                    />
                    <SearchIcon className={styles.rootCatalog__searchIcon} />
                  </label>
                  {page.searchDisclaimer ? (
                    <P4 className={styles.rootCatalog__disclaimer}>
                      {page.searchDisclaimer}
                    </P4>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : page.introTitle ? (
            <H2 className={styles.contentTitle}>{page.introTitle}</H2>
          ) : null}
        </div>

        {page.layout === 'rootTiles' ? (
          <section className={styles.group}>
            {paginatedItems.length ? (
              <div className={styles.rootCatalog__grid}>
                {paginatedItems.map((item) => (
                  <CatalogRootTileCard
                    item={item}
                    key={item.slug}
                    locale={locale}
                    assetCollection={page.assetCollection}
                    itemCatalog={itemCatalog}
                  />
                ))}
              </div>
            ) : (
              <P3 className={styles.rootCatalog__empty}>
                {page.searchEmpty || 'Nothing found'}
              </P3>
            )}
            {totalPages > 1 ? (
              <Pagination
                className={styles.catalogPagination}
                currentPage={normalizedPage}
                maxPages={totalPages}
                jumpToPage={handleJumpToPage}
                setNext={handleSetNext}
                setPrev={handleSetPrev}
              />
            ) : null}
          </section>
        ) : (
          page.groups.map((group) => (
            <section className={styles.group} key={group.title}>
              <div className={styles.group__head}>
                <H2 className={styles.group__title}>{group.title}</H2>
                {group.description && (
                  <P3 className={styles.group__text}>{group.description}</P3>
                )}
              </div>
              <div className={styles.catalogGrid}>
                {group.items.map((item) => (
                  <CatalogCard
                    item={item}
                    key={item.slug}
                    locale={locale}
                    assetCollection={page.assetCollection}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
      <CatalogFaq faq={page.faq ? [...page.faq] : null} />
    </PageShell>
  );
};
